/** Full-screen editor for text (items, titles, names, prompts). */

import {
  attachTextareaLongPressTranslate,
  isCoarsePointerDevice,
} from '../translate/itemLongPressTranslate';
import {
  attachModalSelectionTranslate,
  detachModalSelectionTranslate,
} from '../translate/selectionTranslate';
import {
  clearItemComposerDraft,
  readItemComposerDraft,
  writeItemComposerDraft,
} from './itemEditorDraft';
import { readItemReadingScroll, writeItemReadingScroll } from './itemReadingScroll';

let modalEl: HTMLElement | null = null;
let messageEl: HTMLElement | null = null;
let hintEl: HTMLElement | null = null;

type EditorSession = {
  openedValue: string;
  openedDate: string;
  singleLine: boolean;
  withChecklistDate: boolean;
  translateEnabled: boolean;
  readingMode: boolean;
  hiddenInput: HTMLInputElement | null;
  composerDraftKey: string | null;
  onDone: ((text: string, date?: string) => void) | null;
  onCancel: (() => void) | null;
};

let composerDraftSaveTimer: ReturnType<typeof setTimeout> | null = null;
let readingScrollSaveTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleReadingScrollSave(textarea: HTMLTextAreaElement): void {
  const key = session?.composerDraftKey;
  if (!key || !session?.readingMode) return;
  if (readingScrollSaveTimer) clearTimeout(readingScrollSaveTimer);
  readingScrollSaveTimer = setTimeout(() => {
    readingScrollSaveTimer = null;
    writeItemReadingScroll(key, textarea.scrollTop);
  }, 150);
}

function persistReadingScroll(textarea: HTMLTextAreaElement): void {
  const key = session?.composerDraftKey;
  if (!key || !session?.readingMode) return;
  if (readingScrollSaveTimer) {
    clearTimeout(readingScrollSaveTimer);
    readingScrollSaveTimer = null;
  }
  writeItemReadingScroll(key, textarea.scrollTop);
}

function restoreReadingScroll(textarea: HTMLTextAreaElement, key: string): void {
  const scrollTop = readItemReadingScroll(key);
  if (scrollTop == null || scrollTop <= 0) return;
  const apply = () => {
    textarea.scrollTop = scrollTop;
  };
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
  });
}

function scheduleComposerDraftSave(textarea: HTMLTextAreaElement, dateInput: HTMLInputElement | null): void {
  const key = session?.composerDraftKey;
  if (!key) return;
  if (composerDraftSaveTimer) clearTimeout(composerDraftSaveTimer);
  composerDraftSaveTimer = setTimeout(() => {
    composerDraftSaveTimer = null;
    const date =
      session?.withChecklistDate && dateInput ? dateInput.value : session?.openedDate ?? '';
    writeItemComposerDraft(key, { text: textarea.value, date });
  }, 300);
}

let session: EditorSession | null = null;
let viewportListenersAttached = false;

const HINT_MULTILINE = 'Enter for a new line · Ctrl+Enter or Done when finished';
const HINT_SINGLE_LINE = 'Enter or Done when finished';

let modalLongPressDetach: (() => void) | null = null;

function detachModalTranslate(): void {
  detachModalSelectionTranslate();
  modalLongPressDetach?.();
  modalLongPressDetach = null;
}

function syncModalTranslate(): void {
  if (!modalEl) return;
  const textarea = modalEl.querySelector<HTMLTextAreaElement>('.item-text-modal-input');
  if (!textarea) return;
  detachModalTranslate();
  if (!session?.translateEnabled || !session.readingMode) return;
  modalLongPressDetach = attachTextareaLongPressTranslate(textarea);
  attachModalSelectionTranslate([textarea]);
}

function updateHintForMode(): void {
  if (!hintEl || !session) return;
  const touch = isCoarsePointerDevice();
  if (session.readingMode && session.translateEnabled) {
    hintEl.classList.remove('hidden');
    hintEl.textContent = touch
      ? 'Highlight or long-press to translate'
      : 'Highlight text to translate';
    return;
  }
  if (session.readingMode) {
    hintEl.classList.add('hidden');
    return;
  }
  hintEl.classList.remove('hidden');
  hintEl.textContent = session.singleLine ? HINT_SINGLE_LINE : HINT_MULTILINE;
}

function normalizeValue(raw: string, singleLine: boolean): string {
  const normalized = raw.replace(/\r\n/g, '\n');
  return singleLine ? normalized.replace(/\n/g, ' ').trim() : normalized;
}

function updateKeyboardInset(): void {
  if (!modalEl || modalEl.classList.contains('hidden')) return;
  const vv = window.visualViewport;
  const inset = vv
    ? Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
    : 0;
  modalEl.style.setProperty('--vv-keyboard-inset', `${inset}px`);
}

function attachViewportListeners(): void {
  if (viewportListenersAttached || !window.visualViewport) return;
  viewportListenersAttached = true;
  window.visualViewport.addEventListener('resize', updateKeyboardInset);
  window.visualViewport.addEventListener('scroll', updateKeyboardInset);
}

function scheduleKeyboardInsetUpdates(): void {
  updateKeyboardInset();
  requestAnimationFrame(updateKeyboardInset);
  window.setTimeout(updateKeyboardInset, 120);
  window.setTimeout(updateKeyboardInset, 350);
}

function ensureModal(): HTMLElement {
  if (modalEl) return modalEl;

  const modal = document.createElement('div');
  modal.id = 'item-text-modal';
  modal.className = 'item-text-modal hidden';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Edit text');
  modal.innerHTML = `
    <div class="item-text-modal-backdrop" aria-hidden="true"></div>
    <div class="item-text-modal-sheet">
      <header class="item-text-modal-header">
        <div class="item-text-modal-heading">
          <h2 class="item-text-modal-title">Edit</h2>
          <p class="item-text-modal-message hidden"></p>
        </div>
        <div class="item-text-modal-header-actions">
          <button
            type="button"
            class="btn-icon item-text-modal-reading"
            aria-label="Reading mode"
            title="Reading mode"
            aria-pressed="false"
          >
            <svg class="item-text-modal-reading-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path fill="currentColor" d="M6 4h7a3 3 0 0 1 3 3v14a3 3 0 0 0-3-3H6V4zm2 2v11.17A3 3 0 0 1 11 15h2V7a1 1 0 0 0-1-1H8zm9-2h-1v14h1a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3z"/>
            </svg>
          </button>
          <button type="button" class="item-text-modal-close" aria-label="Close">×</button>
        </div>
      </header>
      <div class="item-text-modal-date-wrap hidden">
        <label class="item-text-modal-date-label">
          <span class="item-text-modal-date-caption">Date</span>
          <input type="date" class="item-text-modal-date" />
        </label>
      </div>
      <textarea class="item-text-modal-input" rows="8" placeholder="…"></textarea>
      <p class="item-text-modal-hint"></p>
      <footer class="item-text-modal-footer">
        <button type="button" class="btn-ghost-dark item-text-modal-cancel">Cancel</button>
        <button type="button" class="btn-primary item-text-modal-done">Done</button>
      </footer>
    </div>`;

  const backdrop = modal.querySelector('.item-text-modal-backdrop')!;
  const closeBtn = modal.querySelector<HTMLButtonElement>('.item-text-modal-close')!;
  const readingBtn = modal.querySelector<HTMLButtonElement>('.item-text-modal-reading')!;
  const cancelBtn = modal.querySelector<HTMLButtonElement>('.item-text-modal-cancel')!;
  const doneBtn = modal.querySelector<HTMLButtonElement>('.item-text-modal-done')!;
  const dateWrap = modal.querySelector<HTMLElement>('.item-text-modal-date-wrap')!;
  const footer = modal.querySelector<HTMLElement>('.item-text-modal-footer')!;
  const textarea = modal.querySelector<HTMLTextAreaElement>('.item-text-modal-input')!;
  messageEl = modal.querySelector<HTMLElement>('.item-text-modal-message')!;
  hintEl = modal.querySelector<HTMLElement>('.item-text-modal-hint')!;

  const setReadingMode = (reading: boolean) => {
    if (!session) return;
    session.readingMode = reading;
    modal.classList.toggle('item-text-modal--reading', reading);
    modal.setAttribute('aria-label', reading ? 'Read text' : 'Edit text');
    readingBtn.setAttribute('aria-pressed', reading ? 'true' : 'false');
    readingBtn.title = reading ? 'Exit reading mode' : 'Reading mode';
    textarea.readOnly = reading;
    textarea.setAttribute('aria-readonly', reading ? 'true' : 'false');

    dateWrap.classList.toggle('hidden', reading || !session.withChecklistDate);
    footer.classList.toggle('hidden', reading);
    updateHintForMode();
    syncModalTranslate();
    if (reading) {
      textarea.blur();
      if (session.composerDraftKey) restoreReadingScroll(textarea, session.composerDraftKey);
    } else {
      persistReadingScroll(textarea);
    }
  };

  const hide = () => {
    persistReadingScroll(textarea);
    detachModalTranslate();
    modal.classList.add('hidden');
    modal.style.setProperty('--vv-keyboard-inset', '0px');
    document.body.style.overflow = '';
    session = null;
  };

  const dateInputEl = modal.querySelector<HTMLInputElement>('.item-text-modal-date')!;

  textarea.addEventListener('focus', scheduleKeyboardInsetUpdates);
  textarea.addEventListener('input', () => scheduleComposerDraftSave(textarea, dateInputEl));
  textarea.addEventListener('scroll', () => scheduleReadingScrollSave(textarea), { passive: true });
  dateInputEl.addEventListener('change', () => scheduleComposerDraftSave(textarea, dateInputEl));

  const applyAndClose = () => {
    const singleLine = session?.singleLine ?? false;
    const val = normalizeValue(textarea.value, singleLine);
    if (session?.hiddenInput) session.hiddenInput.value = val;
    let date: string | undefined;
    if (session?.withChecklistDate) {
      const dateInput = modal.querySelector<HTMLInputElement>('.item-text-modal-date');
      date = dateInput?.value || session.openedDate;
    }
    if (session?.composerDraftKey) clearItemComposerDraft(session.composerDraftKey);
    session?.onDone?.(val, date);
    hide();
  };

  const cancelAndClose = () => {
    if (session?.hiddenInput) session.hiddenInput.value = session.openedValue;
    if (session?.withChecklistDate) {
      const dateInput = modal.querySelector<HTMLInputElement>('.item-text-modal-date');
      if (dateInput) dateInput.value = session.openedDate;
    }
    session?.onCancel?.();
    hide();
  };

  readingBtn.addEventListener('click', () => {
    if (!session) return;
    setReadingMode(!session.readingMode);
  });

  closeBtn.addEventListener('click', cancelAndClose);
  cancelBtn.addEventListener('click', cancelAndClose);
  backdrop.addEventListener('click', cancelAndClose);
  doneBtn.addEventListener('click', applyAndClose);

  textarea.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancelAndClose();
      return;
    }
    if (session?.readingMode) return;
    const singleLine = session?.singleLine ?? false;
    if (e.key === 'Enter') {
      if (singleLine && !e.shiftKey) {
        e.preventDefault();
        applyAndClose();
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        applyAndClose();
      }
    }
  });

  document.body.appendChild(modal);
  modalEl = modal;
  return modal;
}

export function isItemTextEditorModalOpen(): boolean {
  return modalEl != null && !modalEl.classList.contains('hidden');
}

export function closeItemTextEditorModal(revert = true): void {
  if (!modalEl || modalEl.classList.contains('hidden') || !session) return;
  const textarea = modalEl.querySelector<HTMLTextAreaElement>('.item-text-modal-input');
  if (textarea) persistReadingScroll(textarea);
  if (revert) {
    if (session.hiddenInput) session.hiddenInput.value = session.openedValue;
    session.onCancel?.();
  }
  detachModalTranslate();
  modalEl.classList.add('hidden');
  modalEl.style.setProperty('--vv-keyboard-inset', '0px');
  document.body.style.overflow = '';
  session = null;
}

export function openItemTextEditor(opts: {
  value: string;
  title?: string;
  message?: string;
  placeholder?: string;
  singleLine?: boolean;
  /** Long-press / highlight translate (Spanish page only). */
  translateEnabled?: boolean;
  hiddenInput?: HTMLInputElement;
  checklistDate?: string;
  /** Persist in-progress text to localStorage (item editors). */
  composerDraftKey?: string;
  onDone?: (value: string, date?: string) => void;
  onCancel?: () => void;
}): void {
  const modal = ensureModal();
  const textarea = modal.querySelector<HTMLTextAreaElement>('.item-text-modal-input')!;
  const titleEl = modal.querySelector<HTMLElement>('.item-text-modal-title')!;
  const dateWrap = modal.querySelector<HTMLElement>('.item-text-modal-date-wrap')!;
  const dateInput = modal.querySelector<HTMLInputElement>('.item-text-modal-date')!;
  const singleLine = opts.singleLine ?? false;
  const translateEnabled = opts.translateEnabled ?? false;
  const withChecklistDate = opts.checklistDate !== undefined;
  const composerDraftKey = opts.composerDraftKey ?? null;
  const savedDraft = composerDraftKey ? readItemComposerDraft(composerDraftKey) : null;
  const openedValue = savedDraft?.text ?? opts.value;
  const openedDate = savedDraft?.date || opts.checklistDate || '';

  session = {
    openedValue,
    openedDate,
    singleLine,
    withChecklistDate,
    translateEnabled,
    readingMode: false,
    hiddenInput: opts.hiddenInput ?? null,
    composerDraftKey,
    onDone: opts.onDone ?? null,
    onCancel: opts.onCancel ?? null,
  };

  modal.classList.remove('item-text-modal--reading');
  modal.setAttribute('aria-label', 'Edit text');
  const readingBtn = modal.querySelector<HTMLButtonElement>('.item-text-modal-reading')!;
  readingBtn.setAttribute('aria-pressed', 'false');
  readingBtn.title = 'Reading mode';
  textarea.readOnly = false;
  textarea.removeAttribute('aria-readonly');

  const footer = modal.querySelector<HTMLElement>('.item-text-modal-footer')!;
  footer.classList.remove('hidden');

  if (withChecklistDate) {
    dateWrap.classList.remove('hidden');
    dateInput.value = openedDate;
  } else {
    dateWrap.classList.add('hidden');
    dateInput.value = '';
  }

  textarea.value = openedValue;
  textarea.placeholder = opts.placeholder ?? (singleLine ? 'Enter text…' : 'Item text…');
  textarea.rows = singleLine ? 3 : 8;
  textarea.classList.toggle('item-text-modal-input--multiline', !singleLine);
  titleEl.textContent = opts.title ?? 'Edit';
  if (messageEl) {
    const msg = opts.message?.trim();
    if (msg) {
      messageEl.textContent = msg;
      messageEl.classList.remove('hidden');
    } else {
      messageEl.textContent = '';
      messageEl.classList.add('hidden');
    }
  }
  updateHintForMode();

  attachViewportListeners();
  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
  syncModalTranslate();
  const textLen = textarea.value.length;
  const cursorAt = textLen > 200 ? 0 : textLen;
  textarea.focus();
  textarea.setSelectionRange(cursorAt, cursorAt);
  if (cursorAt === 0) textarea.scrollTop = 0;
  scheduleKeyboardInsetUpdates();
}

export function promptTextEditor(opts: {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  singleLine?: boolean;
}): Promise<string | null> {
  return new Promise(resolve => {
    const editorOpts: Parameters<typeof openItemTextEditor>[0] = {
      value: opts.defaultValue ?? '',
      title: opts.title,
      singleLine: opts.singleLine ?? true,
      onDone: value => resolve(value.trim() || null),
      onCancel: () => resolve(null),
    };
    if (opts.message) editorOpts.message = opts.message;
    if (opts.placeholder) editorOpts.placeholder = opts.placeholder;
    openItemTextEditor(editorOpts);
  });
}
